/**
 * 사업자 활동 로그 페이지
 * - 사업장(콤보박스) 선택 + 사업장명을 상단 헤더에 표시
 * - 기간 picker (시작/종료, 날짜만)
 * - 로그 카테고리 다중 체크박스 필터
 * - 컬럼: 로그일시 / 작업 수행자(이름+권한) / 변경 내용 / 카테고리
 */

import { useEffect, useMemo, useState } from 'react';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import {
  useBusinessActivityLogs,
  useBusinessOptions,
} from '@/hooks/useActivityLogs';
import {
  BIZ_ACTOR_ROLE_LABEL,
  BUSINESS_EVENT_TO_CATEGORY,
  BUSINESS_LOG_CATEGORY_LABEL,
  type BizActorRole,
  type BusinessActivityLog,
  type BusinessLogCategory,
} from '@/services/api';
import {
  PageHeader,
  FilterBar,
  FilterField,
  TextInput,
  Select,
  Pagination,
  StatusBadge,
  type BadgeTone,
} from '@/components/common';
import { DataGrid, RowNumberCell } from '@/components/grid';
import { formatBusinessLog } from './formatBusinessLog';

const PAGE_SIZE_OPTIONS = [20, 50, 100];

const CATEGORY_ORDER: BusinessLogCategory[] = [
  'NOTICE_SEND',
  'NOTICE_RECEIVE',
  'PAYMENT',
  'TICKET_GRANT',
  'TICKET_PRODUCT',
  'RESERVATION',
  'ACCOUNT',
];

const CATEGORY_TONE: Record<BusinessLogCategory, BadgeTone> = {
  NOTICE_SEND: 'info',
  NOTICE_RECEIVE: 'info',
  PAYMENT: 'success',
  TICKET_GRANT: 'primary',
  TICKET_PRODUCT: 'warning',
  RESERVATION: 'primary',
  ACCOUNT: 'neutral',
};

const ROLE_TONE: Record<BizActorRole, BadgeTone> = {
  OWNER: 'primary',
  TEACHER: 'info',
  STAFF: 'neutral',
};

/** 오늘로부터 ~30일 전 → 오늘 */
function getDefaultRange(): { from: string; to: string } {
  const pad = (n: number) => String(n).padStart(2, '0');
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const today = new Date();
  const from = new Date();
  from.setDate(today.getDate() - 30);
  return { from: fmt(from), to: fmt(today) };
}

export function BusinessActivityLogPage() {
  const defaultRange = useMemo(getDefaultRange, []);

  // ─── 로컬 폼 상태 ───
  const [localCompanySeq, setLocalCompanySeq] = useState<string>(''); // '' = 전체
  const [localDateFrom, setLocalDateFrom] = useState(defaultRange.from);
  const [localDateTo, setLocalDateTo] = useState(defaultRange.to);
  const [localCategories, setLocalCategories] = useState<
    Set<BusinessLogCategory>
  >(new Set());
  const [localSize, setLocalSize] = useState(20);

  // ─── 적용된 필터 ───
  const [applied, setApplied] = useState({
    companySeq: undefined as number | undefined,
    dateFrom: defaultRange.from,
    dateTo: defaultRange.to,
    categories: [] as BusinessLogCategory[],
  });
  const [pageIndex, setPageIndex] = useState(0);
  const [size, setSize] = useState(20);
  const [refetchNonce, setRefetchNonce] = useState(0);

  const { data: businessOptions = [], isLoading: optionsLoading } =
    useBusinessOptions();

  const { data, isLoading, isFetching, error, refetch } =
    useBusinessActivityLogs(
      {
        companySeq: applied.companySeq,
        dateFrom: applied.dateFrom,
        dateTo: applied.dateTo,
        categories:
          applied.categories.length > 0 ? applied.categories : undefined,
        page: pageIndex,
        size,
      },
      refetchNonce,
    );

  const rows = data?.content ?? [];
  const totalPages = data?.totalPages ?? 0;
  const totalElements = data?.totalElements ?? 0;
  const startIndex = pageIndex * size;

  useEffect(() => {
    setPageIndex(0);
  }, [applied, size]);

  const selectedCompany = useMemo(
    () =>
      applied.companySeq != null
        ? businessOptions.find(b => b.companySeq === applied.companySeq)
        : undefined,
    [businessOptions, applied.companySeq],
  );

  const applyFilters = () => {
    setApplied({
      companySeq: localCompanySeq ? Number(localCompanySeq) : undefined,
      dateFrom: localDateFrom,
      dateTo: localDateTo,
      categories: Array.from(localCategories),
    });
    setSize(localSize);
    setRefetchNonce(n => n + 1);
  };

  const resetFilters = () => {
    setLocalCompanySeq('');
    setLocalDateFrom(defaultRange.from);
    setLocalDateTo(defaultRange.to);
    setLocalCategories(new Set());
    setLocalSize(20);
    setApplied({
      companySeq: undefined,
      dateFrom: defaultRange.from,
      dateTo: defaultRange.to,
      categories: [],
    });
    setSize(20);
    setRefetchNonce(n => n + 1);
  };

  const toggleCategory = (c: BusinessLogCategory) => {
    setLocalCategories(prev => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  };

  const columns = useMemo<ColDef<BusinessActivityLog>[]>(
    () => [
      {
        headerName: '번호',
        width: 72,
        sortable: false,
        cellRenderer: RowNumberCell,
        cellRendererParams: { startIndex },
      },
      {
        headerName: '로그 일시',
        field: 'dtLog',
        width: 170,
        valueFormatter: p =>
          p.value ? String(p.value).replace('T', ' ') : '-',
      },
      {
        headerName: '카테고리',
        width: 130,
        valueGetter: p =>
          p.data ? BUSINESS_EVENT_TO_CATEGORY[p.data.eventCode] : '',
        cellRenderer: (p: ICellRendererParams<BusinessActivityLog>) => {
          if (!p.data) return '-';
          const cat = BUSINESS_EVENT_TO_CATEGORY[p.data.eventCode];
          return (
            <StatusBadge tone={CATEGORY_TONE[cat]}>
              {BUSINESS_LOG_CATEGORY_LABEL[cat]}
            </StatusBadge>
          );
        },
      },
      {
        headerName: '작업 수행자',
        width: 170,
        valueGetter: p => {
          if (!p.data) return '';
          if (!p.data.actorName || !p.data.actorRole) return '시스템';
          return `${p.data.actorName}(${BIZ_ACTOR_ROLE_LABEL[p.data.actorRole]})`;
        },
        cellRenderer: (p: ICellRendererParams<BusinessActivityLog>) => {
          if (!p.data) return '-';
          if (!p.data.actorName || !p.data.actorRole) {
            return (
              <StatusBadge tone="neutral">시스템</StatusBadge>
            );
          }
          return (
            <span className="inline-flex items-center gap-1.5">
              <span className="font-medium text-gray-900">
                {p.data.actorName}
              </span>
              <StatusBadge tone={ROLE_TONE[p.data.actorRole]}>
                {BIZ_ACTOR_ROLE_LABEL[p.data.actorRole]}
              </StatusBadge>
            </span>
          );
        },
      },
      {
        headerName: '변경 내용',
        flex: 1,
        minWidth: 320,
        autoHeight: true,
        cellRenderer: (p: ICellRendererParams<BusinessActivityLog>) => {
          if (!p.data) return '-';
          return (
            <div className="whitespace-normal py-1 leading-relaxed">
              {formatBusinessLog(p.data)}
            </div>
          );
        },
      },
    ],
    [startIndex],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="사업자 활동 로그"
        subtitle="사업장별 도메인 이벤트(이용권/예약/알림장/계정/결제) 활동 기록"
      />

      {/* 선택된 사업장 헤더 — 컬럼이 아닌 상단 별도 영역 */}
      <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
        <div className="text-xs text-gray-500">조회 대상 사업장</div>
        <div className="mt-1 flex items-center gap-2">
          {selectedCompany ? (
            <>
              <span className="text-lg font-bold text-gray-900">
                {selectedCompany.companyName}
              </span>
              <span className="text-sm text-gray-500">
                · 원장 {selectedCompany.ownerName}
              </span>
            </>
          ) : (
            <span className="text-lg font-bold text-gray-700">
              전체 사업장 ({businessOptions.length}곳)
            </span>
          )}
        </div>
      </div>

      {/* 필터 */}
      <FilterBar onSubmit={applyFilters} onReset={resetFilters}>
        <FilterField label="사업장" flex="flex-none" className="md:w-64">
          <Select
            value={localCompanySeq}
            onChange={e => setLocalCompanySeq(e.target.value)}
            disabled={optionsLoading}
          >
            <option value="">전체 사업장</option>
            {businessOptions.map(b => (
              <option key={b.companySeq} value={b.companySeq}>
                {b.companyName} ({b.ownerName})
              </option>
            ))}
          </Select>
        </FilterField>
        <FilterField label="기간 시작" flex="flex-none" className="md:w-44">
          <TextInput
            type="date"
            value={localDateFrom}
            onChange={e => setLocalDateFrom(e.target.value)}
          />
        </FilterField>
        <FilterField label="기간 종료" flex="flex-none" className="md:w-44">
          <TextInput
            type="date"
            value={localDateTo}
            onChange={e => setLocalDateTo(e.target.value)}
          />
        </FilterField>
        <FilterField label="페이지 크기" flex="flex-none" className="md:w-24">
          <Select
            value={localSize}
            onChange={e => setLocalSize(Number(e.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map(n => (
              <option key={n} value={n}>
                {n}개
              </option>
            ))}
          </Select>
        </FilterField>
      </FilterBar>

      {/* 카테고리 다중 선택 — 체크박스 형태 */}
      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-xs font-medium text-gray-600">
            카테고리 필터{' '}
            <span className="text-gray-400">
              (선택 없음 = 전체)
            </span>
          </div>
          {localCategories.size > 0 && (
            <button
              type="button"
              className="text-xs text-primary-600 hover:underline"
              onClick={() => setLocalCategories(new Set())}
            >
              선택 해제
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {CATEGORY_ORDER.map(c => {
            const checked = localCategories.has(c);
            return (
              <label
                key={c}
                className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${
                  checked
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <input
                  type="checkbox"
                  className="hidden"
                  checked={checked}
                  onChange={() => toggleCategory(c)}
                />
                {BUSINESS_LOG_CATEGORY_LABEL[c]}
              </label>
            );
          })}
        </div>
      </div>

      <DataGrid<BusinessActivityLog>
        columns={columns}
        data={rows}
        loading={isLoading}
        fetching={isFetching && !isLoading}
        error={error}
        onRetry={() => refetch()}
        getRowId={row => String(row.logSeq)}
        emptyMessage="조건에 맞는 활동 로그가 없습니다."
      />

      <Pagination
        pageIndex={pageIndex}
        totalPages={totalPages}
        totalElements={totalElements}
        pageSize={size}
        onPageChange={setPageIndex}
      />
    </div>
  );
}
